import { create } from 'zustand';
import { coursesApi } from '../services/api';

export const useCoursesStore = create((set, get) => ({
  // State
  courses: [],
  enrolledCourses: [],
  createdCourses: [],
  selectedCourse: null,
  categories: [],
  recommendations: [],
  loading: false,
  error: null,

  // Actions
  fetchCourses: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const data = await coursesApi.getAll(params);
      set({
        courses: data?.results || data || [],
        loading: false,
      });
      return data;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  fetchCourseById: async (courseId) => {
    set({ loading: true, error: null });
    try {
      const course = await coursesApi.getById(courseId);
      set({
        selectedCourse: course,
        loading: false,
      });
      return course;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchEnrolledCourses: async (token) => {
    set({ loading: true, error: null });
    try {
      const courses = await coursesApi.getEnrolledCourses(token);
      set({
        enrolledCourses: courses || [],
        loading: false,
      });
      return courses;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCreatedCourses: async (token) => {
    set({ loading: true, error: null });
    try {
      const courses = await coursesApi.getCreatedCourses(token);
      set({
        createdCourses: courses || [],
        loading: false,
      });
      return courses;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCategories: async () => {
    try {
      const categories = await coursesApi.getCategories();
      set({ categories: categories || [] });
      return categories;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      throw error;
    }
  },

  fetchRecommendations: async (token) => {
    try {
      const recommendations = await coursesApi.getRecommendations(token);
      set({ recommendations: recommendations || [] });
      return recommendations;
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      throw error;
    }
  },

  enrollInCourse: async (courseId, token) => {
    set({ loading: true, error: null });
    try {
      await coursesApi.enroll(courseId, token);

      // Update enrolled courses list
      const enrolledCourses = await coursesApi.getEnrolledCourses(token);
      set({
        enrolledCourses: enrolledCourses || [],
        loading: false,
      });

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  unenrollFromCourse: async (courseId, token) => {
    set({ loading: true, error: null });
    try {
      await coursesApi.unenroll(courseId, token);

      // Update enrolled courses list
      const enrolledCourses = await coursesApi.getEnrolledCourses(token);
      set({
        enrolledCourses: enrolledCourses || [],
        loading: false,
      });

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createCourse: async (courseData, token) => {
    set({ loading: true, error: null });
    try {
      const newCourse = await coursesApi.create(courseData, token);

      // Update created courses list
      const createdCourses = await coursesApi.getCreatedCourses(token);
      set({
        createdCourses: createdCourses || [],
        loading: false,
      });

      return newCourse;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateCourse: async (courseId, courseData, token) => {
    set({ loading: true, error: null });
    try {
      const updatedCourse = await coursesApi.update(courseId, courseData, token);

      // Update course in lists
      set((state) => ({
        courses: state.courses.map((c) =>
          c.id === courseId ? updatedCourse : c
        ),
        createdCourses: state.createdCourses.map((c) =>
          c.id === courseId ? updatedCourse : c
        ),
        selectedCourse:
          state.selectedCourse?.id === courseId
            ? updatedCourse
            : state.selectedCourse,
        loading: false,
      }));

      return updatedCourse;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteCourse: async (courseId, token) => {
    set({ loading: true, error: null });
    try {
      await coursesApi.delete(courseId, token);

      // Remove course from lists
      set((state) => ({
        courses: state.courses.filter((c) => c.id !== courseId),
        createdCourses: state.createdCourses.filter((c) => c.id !== courseId),
        selectedCourse:
          state.selectedCourse?.id === courseId ? null : state.selectedCourse,
        loading: false,
      }));

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  selectCourse: (courseId) => {
    const course = get().courses.find((c) => c.id === courseId);
    set({ selectedCourse: course });
  },

  clearSelectedCourse: () => set({ selectedCourse: null }),

  clearError: () => set({ error: null }),
}));
